import isObject from 'lodash/isObject';
import get from 'lodash/get';
import truncate from 'lodash/truncate';
import {
  generateAssetURL,
  signUploadURL,
  uploadFileToServer,
  signDownloadURL,
  downloadAsset,
  signProducerApplicationUploadUrl,
} from './api';
import { Assets } from './services';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

/**
 * Uploads an asset to the AWS S3 bucket
 */
export function uploadAssetActions(
  file,
  assetType,
  assetClass,
  context,
  isProducerApplication,
) {
  if (!file) {
    return Promise.reject('No file to upload');
  }

  if (!isObject(file)) {
    return Promise.reject('File must be an object');
  }

  const { name: assetFileName } = file;
  const truncatedAssetFileName = () => {
    if (assetFileName.length > 190) {
      return truncate(assetFileName, {
        length: 180,
        omission: '',
      });
    }
    return assetFileName;
  };
  let finalAssetPath = null;

  // generate S3 URL for clip
  return generateAssetURL(assetType, assetClass, context)
    .then(payload => {
      const assetFolderPath = get(payload, 'data.assetData.assetPath');
      // section for Producer user signup
      if (isProducerApplication) {
        return signProducerApplicationUploadUrl(
          truncatedAssetFileName(),
          assetFolderPath,
        );
      }

      return signUploadURL(truncatedAssetFileName(), assetFolderPath);
    })
    .then(payload => {
      const assetPath = get(payload, 'data.assetPath');
      const signedUploadUrl = get(payload, 'data.signedUploadUrl');
      // assigning variable outside of the call chain; will be returned later!
      finalAssetPath = assetPath;

      return uploadFileToServer(signedUploadUrl, file);
    })
    .then(result => {
      if (result.status !== 200) {
        return Promise.reject(result);
      } else {
        return finalAssetPath;
      }
    });
}

export function getAsset(assetPath, convertToUrl = true, token = null) {
  if (!assetPath) {
    return Promise.reject('No asset path provided');
  }

  const headers = {};

  if (token) {
    headers['auth-code'] = token;
  }

  if (Assets.isBlobUrl(assetPath)) {
    return downloadAsset(assetPath, convertToUrl);
  }

  return signDownloadURL(assetPath, headers)
    .then(payload => {
      const currentAudioUrl = get(payload, 'data.signedDownloadUrl', null);

      if (!currentAudioUrl) {
        return Promise.reject('Download URL could not be created.');
      }

      return downloadAsset(currentAudioUrl, convertToUrl);
    })
    .catch(err => {
      return Promise.reject(err);
    });
}

export const uploadAsset = createAsyncThunk(
  'assets/upload',
  async ({
    imageFile,
    assetType,
    assetClass,
    context,
    isProducerApplication,
  }) =>
    await uploadAssetActions(
      imageFile,
      assetType,
      assetClass,
      context,
      isProducerApplication,
    ),
);

export const loadAsset = createAsyncThunk(
  'assets/download',
  async ({ assetPath, convertToUrl, token }) =>
    await getAsset(assetPath, convertToUrl, token),
);

const assestsSlice = createSlice({
  name: 'assets',
  initialState: {
    loading: false,
  },
  extraReducers: builder => {
    builder
      .addCase(loadAsset.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadAsset.fulfilled, (state, _) => {
        state.loading = false;
      })
      .addCase(loadAsset.rejected, (state, _) => {
        state.loading = false;
      });
  },
  selectors: {
    getLoading: state => state.loading,
  },
});

export const reducer = assestsSlice.reducer;
export const { getLoading } = assestsSlice.selectors;
